import React, {useState} from "react";
import {Alert, Card, Col, Form, Input, Row, Segmented, Select, Typography} from "antd";

const {Text, Paragraph} = Typography;
const {TextArea} = Input;

/**
 * Per-language editing of the admin-configured widget copy.
 *
 * The base (Persian) fields elsewhere in this screen are the source of truth and the fallback;
 * everything here is an override. Leaving a text box empty is meaningful — the widget then shows
 * its own built-in copy in that language (never the Persian text); an empty field label falls
 * back to the Persian label, and an empty placeholder is simply left out. That is why nothing is
 * pre-filled with the base value.
 */
export const TRANSLATABLE_LANGS = [
    {value: "en", label: "English"},
    {value: "ar", label: "العربية"},
];

const TEXT_FIELDS = [
    {key: "title", label: "عنوان", rows: 0},
    {key: "subtitle", label: "زیرعنوان", rows: 0},
    {key: "greeting", label: "پیام خوش‌آمد", rows: 3},
    {key: "offlineMessage", label: "پیام خارج از ساعت کاری", rows: 2},
    {key: "privacyNotice", label: "متن حریم خصوصی", rows: 2},
    {key: "proactiveMessage", label: "متن دعوت خودکار", rows: 2},
];

const TranslationsTab = ({form, baseValues}) => {
    const [lang, setLang] = useState(TRANSLATABLE_LANGS[0].value);
    const fields = baseValues?.preChatFields || [];

    return (
        <>
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 16}}
                message="ترجمه‌ها اختیاری‌اند"
                description="متنی که خالی بگذارید، با متن پیش‌فرضِ خودِ ویجت به همان زبان نمایش داده می‌شود (نه متن فارسی). برچسب خالیِ فیلدهای فرم، فارسی می‌ماند. زبان گفت‌وگو را خودِ کاربر از داخل ویجت انتخاب می‌کند."
            />

            <div style={{marginBottom: 16}}>
                <Segmented
                    options={TRANSLATABLE_LANGS}
                    value={lang}
                    onChange={setLang}
                />
            </div>

            <Card size="small" title="متن‌ها" style={{marginBottom: 16}}>
                <Row gutter={16}>
                    {TEXT_FIELDS.map((f) => (
                        <Col xs={24} sm={f.rows ? 24 : 12} key={f.key}>
                            <Form.Item
                                name={["translations", lang, f.key]}
                                label={
                                    <span>
                                        {f.label}{" "}
                                        <Text type="secondary" style={{fontSize: 11}}>
                                            (فارسی: {(baseValues?.[f.key] || "—").toString().slice(0, 28)}
                                            {(baseValues?.[f.key] || "").length > 28 ? "…" : ""})
                                        </Text>
                                    </span>
                                }
                            >
                                {f.rows ? (
                                    <TextArea rows={f.rows} placeholder="خالی = متن پیش‌فرض ویجت"/>
                                ) : (
                                    <Input placeholder="خالی = متن پیش‌فرض ویجت"/>
                                )}
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Card>

            <Card size="small" title="فیلدهای فرم اولیه">
                <Paragraph type="secondary" style={{fontSize: 12}}>
                    برای فیلدهای انتخابی، گزینه‌ها به همان ترتیب فارسی ترجمه می‌شوند. مقداری که ذخیره می‌شود
                    همیشه نسخه‌ی فارسی است، پس اپراتور پاسخ را به زبان خودش می‌بیند.
                </Paragraph>

                {fields.length === 0 && <Text type="secondary">ابتدا در تب «فرم اطلاعات اولیه» فیلد تعریف کنید.</Text>}

                {fields.map((f, i) => {
                    if (!f || !f.key) return null;
                    return (
                        <Card
                            key={f.key + i}
                            size="small"
                            style={{marginBottom: 10}}
                            title={
                                <span style={{fontSize: 13}}>
                                    {f.label || f.key}{" "}
                                    <Text type="secondary" style={{fontSize: 11}}>({f.type})</Text>
                                </span>
                            }
                        >
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name={["preChatFields", i, "i18n", lang, "label"]}
                                        label="عنوان فیلد"
                                        style={{marginBottom: 8}}
                                    >
                                        <Input placeholder={f.label || "خالی = فارسی"}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name={["preChatFields", i, "i18n", lang, "placeholder"]}
                                        label="راهنما"
                                        style={{marginBottom: 8}}
                                    >
                                        <Input placeholder="خالی = بدون متن راهنما"/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {f.type === "SELECT" && (
                                <Form.Item
                                    name={["preChatFields", i, "i18n", lang, "options"]}
                                    label={
                                        <span>
                                            گزینه‌ها{" "}
                                            <Text type="secondary" style={{fontSize: 11}}>
                                                (به ترتیب: {(f.options || []).join(" ← ")})
                                            </Text>
                                        </span>
                                    }
                                    style={{marginBottom: 0}}
                                >
                                    <Select
                                        mode="tags"
                                        tokenSeparators={[",", "،"]}
                                        placeholder="ترجمه‌ها را به همان ترتیب وارد کنید"
                                    />
                                </Form.Item>
                            )}
                        </Card>
                    );
                })}
            </Card>
        </>
    );
};

export default TranslationsTab;
